class ContentService {
  constructor() {
    this.baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3000';
    this.versionKey = 'website_content_version';
    this.contentKey = 'website_content_data';
    this.timestampKey = 'website_content_timestamp';
    this.cacheExpiry = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
    this.contentRequest = null;
  }

  // Check if cached content exists and is still valid
  isCacheValid() {
    const timestamp = localStorage.getItem(this.timestampKey);
    if (!timestamp) return false;

    const now = new Date().getTime();
    const cacheTime = parseInt(timestamp);
    return now - cacheTime < this.cacheExpiry;
  }

  // Get cached content from localStorage
  getCachedContent() {
    try {
      const content = localStorage.getItem(this.contentKey);
      return content ? JSON.parse(content) : null;
    } catch (error) {
      console.error('Error parsing cached content:', error);
      return null;
    }
  }

  // Get cached version from localStorage
  getCachedVersion() {
    return localStorage.getItem(this.versionKey);
  }

  // Store content in localStorage
  storeContent(content, version) {
    try {
      localStorage.setItem(this.contentKey, JSON.stringify(content));
      localStorage.setItem(this.versionKey, version);
      localStorage.setItem(this.timestampKey, new Date().getTime().toString());
    } catch (error) {
      console.error('Error storing content in cache:', error);
    }
  }

  // Check current version from API
  async checkVersion() {
    try {
      const response = await fetch(
        `${this.baseUrl}/api/v1/homepage/content/version`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );

      if (!response.ok) {
        console.warn(
          `⚠️ Version check failed: ${response.status}. Falling back to cached content.`,
        );
        return null;
      }

      // Validate response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        console.warn(
          '⚠️ Version API returned non-JSON response. Falling back to cached content.',
        );
        return null;
      }

      const versionData = await response.json();
      return versionData.version;
    } catch (error) {
      if (error instanceof SyntaxError) {
        console.warn(
          '⚠️ API server not available or returned invalid response. Using cached content.',
        );
      } else {
        console.warn('⚠️ Error checking content version:', error.message);
      }
      return null;
    }
  }

  // Fetch content from API
  async fetchContent() {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/homepage/content`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        // Check if we got HTML instead of JSON (common with 404/500 errors)
        const text = await response.text();
        if (text.includes('<!doctype') || text.includes('<html')) {
          throw new Error(
            `API endpoint not found (${response.status}). Server returned HTML instead of JSON.`,
          );
        }
        throw new Error(`Content fetch failed: ${response.status} - ${text}`);
      }

      // Validate response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        throw new Error(
          `Expected JSON response but got ${contentType}. Response: ${text.substring(
            0,
            100,
          )}...`,
        );
      }

      const contentData = await response.json();
      return contentData;
    } catch (error) {
      if (
        error instanceof SyntaxError &&
        error.message.includes('Unexpected token')
      ) {
        console.error(
          '🚨 API returned HTML instead of JSON. Is the API server running at',
          this.baseUrl,
          '?',
        );
        throw new Error(
          'API server is not responding with valid JSON. Please check if the backend server is running.',
        );
      }
      console.error('Error fetching content from API:', error);
      throw error;
    }
  }

  // Main method to get website content with smart caching
  async getWebsiteContent() {
    if (this.contentRequest) return this.contentRequest;

    this.contentRequest = this.loadWebsiteContent();
    try {
      return await this.contentRequest;
    } finally {
      this.contentRequest = null;
    }
  }

  async loadWebsiteContent() {
    try {
      // First, check if we have valid cached content
      if (this.isCacheValid()) {
        const cachedContent = this.getCachedContent();
        if (cachedContent) {
          console.log('Using cached website content');
          return cachedContent;
        }
      }

      // Check if we have cached content but need to verify version
      const cachedContent = this.getCachedContent();
      const cachedVersion = this.getCachedVersion();

      if (cachedContent && cachedVersion) {
        // Check current version from API
        const currentVersion = await this.checkVersion();

        if (currentVersion && currentVersion === cachedVersion) {
          // Version matches, update timestamp and use cached content
          localStorage.setItem(
            this.timestampKey,
            new Date().getTime().toString(),
          );
          console.log('Content version unchanged, using cached content');
          return cachedContent;
        }
      }

      // Need to fetch fresh content
      console.log('🔄 Fetching fresh website content from API...');
      const freshContent = await this.fetchContent();

      // Store the fresh content
      this.storeContent(freshContent, freshContent.version);
      console.log('✅ Fresh content loaded and cached successfully');

      return freshContent;
    } catch (error) {
      console.warn('⚠️ Error getting website content:', error.message);

      // Fallback to cached content if available
      const cachedContent = this.getCachedContent();
      if (cachedContent) {
        console.log('💾 API failed, using cached content as fallback');
        return cachedContent;
      }

      // Final fallback to default content
      console.log('🔧 No cached content available, using default content');
      return this.getDefaultContent();
    }
  }

  // Default content as fallback
  getDefaultContent() {
    return {
      version: '1.0.0',
      sections: {
        hero: {
          title: 'Find Trusted Local Service Providers',
          subtitle:
            'Connect with verified professionals for all your home improvement needs',
          cta_button: 'Get Started',
          search_form: {
            placeholder: 'What service do you need?',
            location_placeholder: 'Enter your location',
          },
        },
        how_it_works: {
          title: 'How It Works',
          subtitle: 'Get your project done in three simple steps',
          steps: [
            {
              step: 1,
              title: 'Post Your Project',
              description:
                'Tell us what you need done and get matched with local professionals',
            },
            {
              step: 2,
              title: 'Compare Quotes',
              description:
                'Receive multiple quotes and choose the best provider for your needs',
            },
            {
              step: 3,
              title: 'Get It Done',
              description:
                'Work with your chosen professional to complete your project',
            },
          ],
        },
        services_grid: {
          title: 'Popular Services',
          subtitle: 'Discover the most requested services in your area',
          services: [
            {
              name: 'Kitchen Renovation',
              category: 'Home Renovation',
              avg_bids: 8,
              avg_savings: 1200,
              popularity: 92,
              image: '/images/kitchen-renovation.jpg',
            },
            {
              name: 'Plumbing Repair',
              category: 'Maintenance',
              avg_bids: 12,
              avg_savings: 300,
              popularity: 88,
              image: '/images/plumbing.jpg',
            },
            {
              name: 'Electrical Installation',
              category: 'Installation',
              avg_bids: 6,
              avg_savings: 450,
              popularity: 75,
              image: '/images/electrical.jpg',
            },
            {
              name: 'Landscaping',
              category: 'Outdoor',
              avg_bids: 5,
              avg_savings: 800,
              popularity: 68,
              image: '/images/landscaping.jpg',
            },
          ],
        },
        benefits: {
          title: 'Benefits for Everyone',
          subtitle:
            'Our bidding platform creates value for both homeowners seeking services and professionals looking to grow their business.',
          homeowners: {
            badge: 'For Homeowners',
            title: 'Get Better Results for Less',
            subtitle:
              'Why settle for the first quote when you can compare multiple competitive offers?',
            benefits: [
              {
                icon: 'dollar-sign',
                title: 'Save 15-30% on Average',
                description:
                  'Competitive bidding drives prices down while maintaining quality standards.',
              },
              {
                icon: 'clock',
                title: 'Faster Project Completion',
                description:
                  'Multiple providers competing means faster response times and project starts.',
              },
              {
                icon: 'shield-check',
                title: 'Verified Professionals Only',
                description:
                  'All service providers are licensed, insured, and background-checked.',
              },
              {
                icon: 'target',
                title: 'Perfect Match Guarantee',
                description:
                  'Our smart matching ensures you get providers suited for your specific needs.',
              },
            ],
            cta: {
              text: 'Post Your Project',
              url: '/',
            },
          },
          providers: {
            badge: 'For Service Providers',
            title: 'Win More Quality Projects',
            subtitle:
              'Connect with motivated customers and grow your business through fair competition.',
            benefits: [
              {
                icon: 'users',
                title: 'Access Quality Leads',
                description:
                  'Connect with serious homeowners ready to hire for their projects.',
              },
              {
                icon: 'trending-up',
                title: 'Grow Your Business',
                description:
                  'Expand your customer base and increase revenue through our platform.',
              },
              {
                icon: 'award',
                title: 'Fair Competition',
                description:
                  'Compete on merit, not just who responds first. Quality work gets rewarded.',
              },
              {
                icon: 'star',
                title: 'Build Reputation',
                description:
                  'Earn reviews and build your profile to attract more high-value projects.',
              },
            ],
            cta: {
              text: 'Join as Provider',
              url: '/signup?role=provider',
            },
          },
        },
        testimonials: null,
        newsletter: {
          title: 'Stay Updated',
          subtitle: 'Get the latest tips, trends, and exclusive offers',
          placeholder: 'Enter your email address',
          cta_button: 'Subscribe',
        },
        faq: {
          title: 'Frequently Asked Questions',
          questions: [
            {
              question: 'How does FixDirectly work?',
              answer:
                'Simply post your project, receive quotes from verified professionals, and choose the best one for your needs.',
            },
            {
              question: 'Are the service providers verified?',
              answer:
                'Yes, all our service providers go through a comprehensive verification process including background checks and insurance verification.',
            },
            {
              question: 'Is there a cost to post a project?',
              answer:
                'No, posting a project is completely free. You only pay the service provider directly for completed work.',
            },
          ],
        },
        footer: {
          company_name: 'FixDirectly',
          tagline: 'Your trusted platform for home services',
          contact: {
            email: 'support@fixdirectly.com',
            phone: '1-800-FIX-DIRECT',
          },
          social_media: {
            facebook: '#',
            twitter: '#',
            instagram: '#',
            linkedin: '#',
          },
        },
      },
      feature_flags: {
        auto_registration_enabled: true
      }
    };
  }

  // Clear cached content (useful for debugging or forced refresh)
  clearCache() {
    localStorage.removeItem(this.contentKey);
    localStorage.removeItem(this.versionKey);
    localStorage.removeItem(this.timestampKey);
  }

  // Get cache status for debugging
  getCacheStatus() {
    return {
      hasCache: !!this.getCachedContent(),
      version: this.getCachedVersion(),
      isValid: this.isCacheValid(),
      timestamp: localStorage.getItem(this.timestampKey),
    };
  }

  // ============================================
  // Legal Content Methods
  // ============================================

  /**
   * Fetch Terms of Use content from the public API
   * @returns {Promise<{success: boolean, data: object, message?: string}>}
   */
  async getTermsOfUse() {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/content/terms-of-use`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch Terms of Use: ${response.status}`);
      }

      const data = await response.json();
      return {
        success: true,
        data: data,
      };
    } catch (error) {
      console.error('Get Terms of Use error:', error);
      return {
        success: false,
        message: error.message || 'Failed to fetch Terms of Use',
        data: null,
      };
    }
  }

  /**
   * Fetch Privacy Policy content from the public API
   * @returns {Promise<{success: boolean, data: object, message?: string}>}
   */
  async getPrivacyPolicy() {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/content/privacy-policy`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch Privacy Policy: ${response.status}`);
      }

      const data = await response.json();
      return {
        success: true,
        data: data,
      };
    } catch (error) {
      console.error('Get Privacy Policy error:', error);
      return {
        success: false,
        message: error.message || 'Failed to fetch Privacy Policy',
        data: null,
      };
    }
  }

  // ============================================
  // Partner Page Methods
  // ============================================

  /**
   * Fetch Partner Page content from the public API
   * @returns {Promise<{success: boolean, data: object, message?: string}>}
   */
  async getPartnerPage() {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/content/partner-page`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch Partner Page content: ${response.status}`);
      }

      const data = await response.json();
      return {
        success: true,
        data: data,
      };
    } catch (error) {
      console.error('Get Partner Page error:', error);
      return {
        success: false,
        message: error.message || 'Failed to fetch Partner Page content',
        data: null,
      };
    }
  }

  // ============================================
  // Admin Methods for Legal Content Management
  // ============================================

  /**
   * Get auth token for admin requests
   */
  getAuthHeaders() {
    const token =
      localStorage.getItem('auth_token') || localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  /**
   * Fetch Terms of Use for admin editing
   * @returns {Promise<{success: boolean, data: object, message?: string}>}
   */
  async getTermsOfUseForAdmin() {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/admin/content/sections/terms_of_use`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch Terms of Use: ${response.status}`);
      }

      const data = await response.json();
      return {
        success: true,
        data: data,
      };
    } catch (error) {
      console.error('Get Terms of Use for admin error:', error);
      return {
        success: false,
        message: error.message || 'Failed to fetch Terms of Use',
        data: null,
      };
    }
  }

  /**
   * Update Terms of Use (admin only)
   * @param {object} content - The updated content
   * @returns {Promise<{success: boolean, data: object, message?: string}>}
   */
  async updateTermsOfUse(content) {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/admin/content/sections/terms_of_use`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ content }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update Terms of Use: ${response.status}`);
      }

      const data = await response.json();
      return {
        success: true,
        data: data,
      };
    } catch (error) {
      console.error('Update Terms of Use error:', error);
      return {
        success: false,
        message: error.message || 'Failed to update Terms of Use',
      };
    }
  }

  /**
   * Fetch Privacy Policy for admin editing
   * @returns {Promise<{success: boolean, data: object, message?: string}>}
   */
  async getPrivacyPolicyForAdmin() {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/admin/content/sections/privacy_policy`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch Privacy Policy: ${response.status}`);
      }

      const data = await response.json();
      return {
        success: true,
        data: data,
      };
    } catch (error) {
      console.error('Get Privacy Policy for admin error:', error);
      return {
        success: false,
        message: error.message || 'Failed to fetch Privacy Policy',
        data: null,
      };
    }
  }

  /**
   * Update Privacy Policy (admin only)
   * @param {object} content - The updated content
   * @returns {Promise<{success: boolean, data: object, message?: string}>}
   */
  async updatePrivacyPolicy(content) {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/admin/content/sections/privacy_policy`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ content }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update Privacy Policy: ${response.status}`);
      }

      const data = await response.json();
      return {
        success: true,
        data: data,
      };
    } catch (error) {
      console.error('Update Privacy Policy error:', error);
      return {
        success: false,
        message: error.message || 'Failed to update Privacy Policy',
      };
    }
  }
  /**
   * Fetch Partner Page content for admin editing
   * @returns {Promise<{success: boolean, data: object, message?: string}>}
   */
  async getPartnerPageForAdmin() {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/admin/content/sections/partner_page`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch Partner Page for admin: ${response.status}`);
      }

      const data = await response.json();
      return {
        success: true,
        data: data,
      };
    } catch (error) {
      console.error('Get Partner Page for admin error:', error);
      return {
        success: false,
        message: error.message || 'Failed to fetch Partner Page content',
        data: null,
      };
    }
  }

  /**
   * Update Partner Page content (admin only)
   * @param {object} content - The updated content
   * @returns {Promise<{success: boolean, data: object, message?: string}>}
   */
  async updatePartnerPage(content) {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/admin/content/sections/partner_page`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ content_section: content }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update Partner Page content: ${response.status}`);
      }

      const data = await response.json();
      return {
        success: true,
        data: data,
      };
    } catch (error) {
      console.error('Update Partner Page error:', error);
      return {
        success: false,
        message: error.message || 'Failed to update Partner Page content',
      };
    }
  }
}

// Export singleton instance
export const contentService = new ContentService();
